<template>
  <div class="cr-panel candidates-panel" :class="{'is-compact': compact}">
    <div class="cr-panel-head">
      <div class="cr-panel-title">
        <fa-icon class="cr-panel-icon" :icon="['fal', 'file-invoice']"/>
        Guias sem lote
        <span class="label label-rounded" v-if="!loading">{{ data.total }}</span>
      </div>
      <div class="cr-panel-subtitle">{{ subtitle }}</div>
    </div>

    <div class="cr-toolbar">
      <div class="cr-toolbar-field"
           :class="{'has-error': $v.filter.startDate.$error || $v.filter.endDate.$error}">
        <label class="form-label">Período (até 3 meses)</label>
        <st-date-period
          :start-date.sync="filter.startDate"
          :end-date.sync="filter.endDate"
          :max-months="3"
          :periods="periods"
          :start-date-validator="$v.filter.startDate"
          :end-date-validator="$v.filter.endDate"
          @blur:startDate="$v.filter.startDate.$touch()"
          @blur:endDate="$v.filter.endDate.$touch()"
        />
      </div>
      <div class="cr-toolbar-field">
        <label class="form-label">Profissional</label>
        <select class="form-select" v-model="filter.professionalId">
          <option value="">Todos</option>
          <option v-for="item in professionals" :value="item.id" :key="item.id">
            {{ item.name }}
          </option>
        </select>
      </div>
      <div class="cr-toolbar-field" v-if="!fixedType">
        <label class="form-label">Tipo de guia</label>
        <select class="form-select" :value="filter.type"
                @change="setType($event.target.value)">
          <option v-for="option in typeOptions" :value="option.value" :key="option.value">
            {{ option.text }}
          </option>
        </select>
      </div>
      <div class="cr-toolbar-field cr-toolbar-grow">
        <label class="form-label">Pesquisar</label>
        <input type="text" class="form-input" v-model="filter.search" maxlength="100"
               placeholder="Paciente, nº da guia ou carteirinha" @keypress.enter="reload">
      </div>
      <div class="cr-toolbar-actions">
        <button class="btn btn-secondary btn-icon btn-icon-left" :class="{loading}"
                :disabled="loading" @click="reload">
          <fa-icon :icon="['fal', 'search']"/>Pesquisar
        </button>
      </div>
    </div>

    <div class="loading loading-lg cr-panel-loading" v-if="loading"></div>
    <template v-else>
      <div class="empty-inline" v-if="error">
        <div class="empty-icon"><fa-icon :icon="['fal', 'exclamation-triangle']" size="2x"/></div>
        <div class="empty-title">Não foi possível carregar as guias.</div>
        <button class="btn btn-gray btn-sm" @click="reload">Tentar novamente</button>
      </div>
      <div class="empty-inline" v-else-if="data.items.length === 0 && hasFilter">
        <div class="empty-icon"><fa-icon :icon="['fal', 'filter']" size="2x"/></div>
        <div class="empty-title">Nenhuma guia com esses filtros.</div>
        <button class="btn btn-link btn-sm" @click="clearFilters">Limpar filtros</button>
      </div>
      <div class="empty-inline" v-else-if="data.items.length === 0">
        <div class="empty-icon"><fa-icon :icon="['fal', 'check-circle']" size="2x"/></div>
        <div class="empty-title">Nenhuma guia aberta para este plano.</div>
        <div>As guias nascem na aba Serviços do agendamento ou na tela Guias.</div>
        <router-link class="btn btn-link btn-sm" to="/financial/billing-accounts" v-if="!compact">
          Ir para Guias
        </router-link>
      </div>
      <template v-else>
        <div class="cr-table-scroll">
          <table class="table table-hover">
            <thead>
            <tr>
              <th style="width: 30px">
                <label class="form-checkbox">
                  <input type="checkbox" :checked="allSelected"
                         :indeterminate.prop="partiallySelected"
                         :disabled="selectableItems.length === 0" @change="toggleAll">
                  <i class="form-icon"></i>
                </label>
              </th>
              <th>Data</th>
              <th>Tipo</th>
              <th>Guia</th>
              <th>Paciente</th>
              <th class="hide-sm" v-if="!compact">Profissional</th>
              <th class="text-right hide-sm">Itens</th>
              <th class="text-right">Valor convênio</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <candidate-row
              v-for="item in data.items" :key="item.id"
              :item="item"
              :checked="selectedIds.includes(item.id)"
              :disabled-reason="disabledReason(item)"
              :compact="compact"
              @toggle="toggle"
              @open="$emit('open-guide', $event)"
            />
            </tbody>
          </table>
        </div>
        <div class="candidates-pickbar">
          <a href="#" @click.prevent="toggleAll" v-if="selectableItems.length > 0">
            {{ pickAllLabel }}
          </a>
          <span class="text-gray" v-else>Nenhuma guia compatível com o tipo do lote</span>
          <span class="candidates-pickbar-right">
            <span class="text-gray">Mostrando {{ data.items.length }} de {{ data.total }}</span>
            <button class="btn btn-sm btn-gray" v-if="data.hasMore"
                    :class="{loading: loadingMore}" :disabled="loadingMore" @click="loadMore">
              Carregar mais
            </button>
          </span>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { date, minDate, maxDate } from '@/data/validators';
import * as professionalApi from '@/app/professional/api';
import {
  GUIDE_TYPES,
  MIXABLE_GUIDE_TYPES,
  guideTypeName,
  batchTypeOf,
  canMixTypes,
} from '../service';
import CandidateRow from './CandidateRow.vue';

function defaultFilter() {
  return {
    startDate: moment().startOf('month').format('YYYY-MM-DD'),
    endDate: moment().endOf('month').format('YYYY-MM-DD'),
    professionalId: '',
    type: '',
    search: '',
  };
}

export default {
  components: {
    CandidateRow,
  },
  props: {
    planId: {
      type: String,
      required: true,
    },
    insuranceId: {
      type: String,
      default: null,
    },
    fixedType: {
      type: String,
      default: null,
    },
    compact: {
      type: Boolean,
      default: false,
    },
    allowMixed: {
      type: Boolean,
      default: false,
    },
    selected: {
      type: Array,
      default: () => [],
    },
    initialFilter: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      error: false,
      professionals: [],
      filter: { ...defaultFilter(), ...(this.initialFilter || {}) },
      data: {
        items: [],
        total: 0,
        hasMore: false,
        offset: 0,
        limit: 200,
      },
    };
  },
  computed: {
    periods() {
      return [
        { value: 'month', text: 'Este mês' },
        { value: 'month:-1', text: 'Mês anterior' },
        { value: 'd:-30', text: 'Últimos 30 dias' },
        { value: 'd:-60', text: 'Últimos 60 dias' },
        { value: 'd:-90', text: 'Últimos 90 dias' },
      ];
    },
    typeOptions() {
      return [
        { value: '', text: 'Todas' },
        ...Object.keys(GUIDE_TYPES).map(value => ({ value, text: GUIDE_TYPES[value] })),
      ];
    },
    selectedIds() {
      return this.selected.map(({ id }) => id);
    },
    selectedType() {
      if (this.fixedType) return this.fixedType;
      return batchTypeOf(this.selected);
    },
    mixableTarget() {
      return this.allowMixed && (!this.fixedType || this.fixedType === 'mixed'
        || MIXABLE_GUIDE_TYPES.includes(this.fixedType));
    },
    subtitle() {
      if (!this.compact) return 'Guias abertas do plano selecionado no período';
      return this.mixableTarget
        ? 'Só guias abertas do mesmo plano · consulta e SP/SADT podem ir juntas'
        : `Só guias abertas do mesmo plano e tipo (${guideTypeName(this.fixedType)})`;
    },
    selectableItems() {
      return this.data.items.filter(item => !this.disabledReason(item));
    },
    allSelected() {
      return this.selectableItems.length > 0
        && this.selectableItems.every(({ id }) => this.selectedIds.includes(id));
    },
    partiallySelected() {
      return !this.allSelected
        && this.selectableItems.some(({ id }) => this.selectedIds.includes(id));
    },
    pickAllLabel() {
      if (this.allSelected) return 'Limpar seleção';
      const type = this.selectedType ? ` de ${guideTypeName(this.selectedType)}` : '';
      return `Selecionar todas${type} (${this.selectableItems.length})`;
    },
    hasFilter() {
      const initial = defaultFilter();
      return this.filter.professionalId !== ''
        || this.filter.type !== ''
        || this.filter.search.trim() !== ''
        || this.filter.startDate !== initial.startDate
        || this.filter.endDate !== initial.endDate;
    },
  },
  validations() {
    const { startDate } = this.filter;
    const validStart = startDate && moment(startDate, 'YYYY-MM-DD', true).isValid();
    return {
      filter: {
        startDate: { required, date },
        endDate: {
          required,
          date,
          minDate: minDate(validStart ? startDate : null),
          maxDate: maxDate(validStart ? moment(startDate).add(3, 'months').format('YYYY-MM-DD') : null),
        },
      },
    };
  },
  watch: {
    planId() {
      this.reload();
    },
  },
  mounted() {
    this.loadProfessionals();
    this.reload();
  },
  methods: {
    guideTypeName,
    disabledReason(item) {
      if (!canMixTypes(item.type, this.selectedType, this.allowMixed)) {
        return `Tipo diferente do lote (${guideTypeName(this.selectedType)})`;
      }
      return null;
    },
    typeParam() {
      if (this.fixedType) {
        return this.mixableTarget ? MIXABLE_GUIDE_TYPES.join(',') : this.fixedType;
      }
      return this.filter.type;
    },
    setType(value) {
      this.filter.type = value;
      this.reload();
    },
    clearFilters() {
      this.filter = defaultFilter();
      this.$v.filter.$reset();
      this.reload();
    },
    reload() {
      this.data.offset = 0;
      return this.load();
    },
    loadMore() {
      this.data.offset += this.data.limit;
      return this.load();
    },
    load() {
      this.$v.filter.$touch();
      if (this.$v.filter.$error) return Promise.resolve();

      const firstData = this.data.offset === 0;
      if (firstData) this.loading = true; else this.loadingMore = true;
      this.error = false;

      const params = {
        planId: this.planId,
        limit: this.data.limit,
        offset: this.data.offset,
        startDate: this.filter.startDate,
        endDate: this.filter.endDate,
      };
      if (this.insuranceId) params.insuranceId = this.insuranceId;
      if (this.filter.professionalId) params.professionalId = this.filter.professionalId;
      if (this.typeParam()) params.type = this.typeParam();
      if (this.filter.search.trim()) params.search = this.filter.search.trim();

      return this.$http.get('/billing-batches/candidates', { params })
        .then(({ data }) => {
          const items = firstData ? data.items : [...this.data.items, ...data.items];
          this.data.items = items;
          this.data.total = data.total;
          this.data.hasMore = items.length < data.total;
          this.$emit('loaded', { items, plan: data.plan, total: data.total });
        })
        .catch((e) => {
          this.error = true;
          this.$toast.error(e);
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    loadProfessionals() {
      return professionalApi.allActive()
        .then((data) => {
          this.professionals = data.items;
        })
        .catch(() => {});
    },
    toggle(item) {
      if (this.selectedIds.includes(item.id)) {
        this.$emit('update:selected', this.selected.filter(({ id }) => id !== item.id));
        return;
      }
      if (this.disabledReason(item)) return;
      this.$emit('update:selected', [...this.selected, item]);
    },
    toggleAll() {
      if (this.allSelected) {
        const visible = this.selectableItems.map(({ id }) => id);
        this.$emit('update:selected', this.selected.filter(({ id }) => !visible.includes(id)));
        return;
      }
      const missing = this.selectableItems.filter(({ id }) => !this.selectedIds.includes(id));
      this.$emit('update:selected', [...this.selected, ...missing]);
    },
    select(ids) {
      const found = this.data.items
        .filter(item => ids.includes(item.id) && !this.disabledReason(item));
      const missing = found.filter(({ id }) => !this.selectedIds.includes(id));
      this.$emit('update:selected', [...this.selected, ...missing]);
      return found.length;
    },
    removeItems(ids) {
      this.data.items = this.data.items.filter(({ id }) => !ids.includes(id));
      this.data.total = Math.max(0, this.data.total - ids.length);
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.candidates-panel {
  .cr-toolbar .input-group .form-input {
    width: 5.2rem;
  }
  .candidates-pickbar {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border-top: $border-color solid $border-width;
    display: flex;
    flex-wrap: wrap;
    font-size: $font-size-sm;
    gap: $layout-spacing;
    justify-content: space-between;
    padding: $layout-spacing $layout-spacing-lg;
    a:focus {
      box-shadow: none;
    }
    .candidates-pickbar-right {
      align-items: center;
      display: flex;
      gap: $layout-spacing;
    }
  }
  &.is-compact {
    .cr-panel-head .cr-panel-title {
      font-size: $font-size;
    }
  }
}
</style>
